import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, Folder, FolderInput, FolderOpen, FolderPlus, MoreHorizontal, Pencil, Trash2, } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderContextMenu } from './FolderContextMenu';
import { useFolderTreeContext } from './FolderTreeContext';
// MIME type for study drag operations
const STUDY_MIME_TYPE = 'application/x-study-id';
export function FolderTreeItem({ folder, depth = 0, onRename, onDelete, onCreateSubfolder, onMoveFolder, onMoveTo, onStudyDrop, }) {
    const { t } = useTranslation();
    const { isExpanded, toggleExpanded, setExpanded, activeFolderId, setActiveFolderId, draggedFolderId, setDraggedFolderId, dropTargetId, dropPosition, setDropTarget, } = useFolderTreeContext();
    const itemRef = useRef(null);
    const [isStudyDropTarget, setIsStudyDropTarget] = useState(false);
    const hasChildren = folder.children.length > 0;
    const expanded = isExpanded(folder.id);
    const isActive = activeFolderId === folder.id;
    const isDragging = draggedFolderId === folder.id;
    const handleClick = () => {
        setActiveFolderId(folder.id);
    };
    const handleExpandClick = (e) => {
        e.stopPropagation();
        toggleExpanded(folder.id);
    };
    const handleDragStart = (e) => {
        setDraggedFolderId(folder.id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', folder.id);
    };
    const handleDragEnd = () => {
        setDraggedFolderId(null);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        // Check if this is a study being dragged
        const hasStudyData = e.dataTransfer.types.includes(STUDY_MIME_TYPE);
        if (hasStudyData) {
            // Study drop - always "inside" the folder
            e.dataTransfer.dropEffect = 'copy';
            setIsStudyDropTarget(true);
            return;
        }
        // Folder drag - existing logic
        if (!draggedFolderId || draggedFolderId === folder.id) {
            return;
        }
        const rect = itemRef.current?.getBoundingClientRect();
        if (!rect)
            return;
        const y = e.clientY - rect.top;
        const height = rect.height;
        let position;
        if (y < height * 0.25) {
            position = 'before';
        }
        else if (y > height * 0.75) {
            position = 'after';
        }
        else {
            position = 'inside';
        }
        setDropTarget(folder.id, position);
    };
    const handleDragLeave = (e) => {
        const relatedTarget = e.relatedTarget;
        if (!itemRef.current?.contains(relatedTarget)) {
            setIsStudyDropTarget(false);
            if (dropTargetId === folder.id) {
                setDropTarget(null, null);
            }
        }
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Check if this is a study being dropped
        const studyId = e.dataTransfer.getData(STUDY_MIME_TYPE);
        if (studyId && onStudyDrop) {
            onStudyDrop(studyId, folder.id);
            setIsStudyDropTarget(false);
            return;
        }
        // Folder drop - existing logic
        if (draggedFolderId && draggedFolderId !== folder.id && dropPosition) {
            onMoveFolder(draggedFolderId, folder.id, dropPosition);
            // Auto-expand target folder when nesting inside
            if (dropPosition === 'inside') {
                setExpanded(folder.id, true);
            }
        }
        setDropTarget(null, null);
        setIsStudyDropTarget(false);
    };
    const isDropTarget = dropTargetId === folder.id;
    return (_jsxs("div", { children: [_jsx(FolderContextMenu, { folder: folder, onRename: () => onRename(folder), onDelete: () => onDelete(folder), onCreateSubfolder: () => onCreateSubfolder(folder), children: _jsxs("div", { ref: itemRef, role: "treeitem", "aria-selected": isActive, "data-testid": `folder-item-${folder.id}`, className: cn(
                    // select-none + no touch callout: a long-press on the row (which is
                    // draggable) must not start a text selection or the iOS link menu.
                    'group relative flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-sm transition-colors select-none [-webkit-touch-callout:none]', isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground', isDragging && 'opacity-50', 
                    // Folder drop indicators
                    isDropTarget && dropPosition === 'inside' && 'ring-primary ring-2 ring-inset', isDropTarget &&
                        dropPosition === 'before' &&
                        'before:bg-primary before:absolute before:top-0 before:right-0 before:left-0 before:h-0.5', isDropTarget &&
                        dropPosition === 'after' &&
                        'after:bg-primary after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5', 
                    // Study drop indicator - green highlight
                    isStudyDropTarget && 'bg-green-500/10 ring-2 ring-green-500 ring-inset'), style: { paddingLeft: `${depth * 12 + 8}px` }, onClick: handleClick, onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                        }
                    }, tabIndex: 0, draggable: true, onDragStart: handleDragStart, onDragEnd: handleDragEnd, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [hasChildren ? (_jsx("button", { type: "button", className: "hover:bg-muted relative flex items-center justify-center rounded p-0.5 after:absolute after:-inset-1 pointer-coarse:-my-1.5 pointer-coarse:size-8", onClick: handleExpandClick, "aria-label": expanded
                                ? t('folder.collapse', { name: folder.name })
                                : t('folder.expand', { name: folder.name }), "aria-expanded": expanded, children: expanded ? (_jsx(ChevronDown, { className: "h-3 w-3" })) : (_jsx(ChevronRight, { className: "h-3 w-3" })) })) : (_jsx("span", { className: "w-4 shrink-0 pointer-coarse:w-8", "aria-hidden": "true" })), expanded && hasChildren ? (_jsx(FolderOpen, { className: "text-muted-foreground h-4 w-4" })) : (_jsx(Folder, { className: "text-muted-foreground h-4 w-4" })), _jsx("span", { className: "flex-1 truncate", children: folder.name }), folder.studyCount > 0 && (_jsx("span", { className: "text-muted-foreground text-xs", children: folder.studyCount })), _jsx(FolderActionsMenu, { folder: folder, onRename: () => onRename(folder), onCreateSubfolder: () => onCreateSubfolder(folder), onMoveTo: () => onMoveTo(folder), onDelete: () => onDelete(folder) })] }) }), hasChildren && expanded && (_jsx("div", { children: folder.children.map((child) => (_jsx(FolderTreeItem, { folder: child, depth: depth + 1, onRename: onRename, onDelete: onDelete, onCreateSubfolder: onCreateSubfolder, onMoveFolder: onMoveFolder, onMoveTo: onMoveTo, onStudyDrop: onStudyDrop }, child.id))) }))] }));
}
// Events from the portalled menu still bubble through the React tree to the
// row, whose click selects the folder and whose Enter/Space do the same.
const stopRowEvent = (e) => e.stopPropagation();
/**
 * The row's visible actions menu. Right-click (the ContextMenu) and drag were
 * the only ways to rename, nest, move or delete a folder, and neither exists
 * on a phone or tablet. Mouse users see the button on row hover or keyboard
 * focus; any device with a touch screen (including a Windows touch laptop,
 * whose primary pointer is still the mouse) always sees it. While hidden it
 * takes no taps.
 */
function FolderActionsMenu({ folder, onRename, onCreateSubfolder, onMoveTo, onDelete, }) {
    const { t } = useTranslation();
    return (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx("button", { type: "button", "aria-label": t('folder.actionsFor', { name: folder.name }), "data-testid": `folder-actions-${folder.id}`, onClick: stopRowEvent, onKeyDown: stopRowEvent, className: "text-muted-foreground hover:text-foreground hover:bg-muted pointer-events-none -my-1 flex size-6 shrink-0 items-center justify-center rounded opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 data-[state=open]:pointer-events-auto data-[state=open]:opacity-100 pointer-coarse:-my-2 pointer-coarse:size-9 any-pointer-coarse:pointer-events-auto any-pointer-coarse:opacity-100", children: _jsx(MoreHorizontal, { className: "h-4 w-4" }) }) }), _jsxs(DropdownMenuContent, { align: "end", className: "w-48", onClick: stopRowEvent, onKeyDown: stopRowEvent, children: [_jsxs(DropdownMenuItem, { onSelect: onRename, children: [_jsx(Pencil, { className: "mr-2 h-4 w-4" }), t('worklist.rename')] }), _jsxs(DropdownMenuItem, { onSelect: onCreateSubfolder, children: [_jsx(FolderPlus, { className: "mr-2 h-4 w-4" }), t('folder.newSubfolderAction')] }), _jsxs(DropdownMenuItem, { onSelect: onMoveTo, "data-testid": `folder-move-${folder.id}`, children: [_jsx(FolderInput, { className: "mr-2 h-4 w-4" }), t('folder.moveTo')] }), _jsx(DropdownMenuSeparator, {}), _jsxs(DropdownMenuItem, { onSelect: onDelete, className: "text-destructive focus:text-destructive", children: [_jsx(Trash2, { className: "mr-2 h-4 w-4" }), t('button.delete')] })] })] }));
}
